import { createContext, useContext, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export type Crumb = {
  label: string
  // Sin `to`, el tramo es texto plano — el uso normal es en el último (la
  // página actual no navega a sí misma), pero cualquiera puede quedar sin
  // link si no hay a dónde mandarlo.
  to?: string
}

/** Camino de navegación de la página actual, no un historial de clicks: la
 *  jerarquía sale de la estructura fija de la app (Runs > #42, Estrategias >
 *  golden_cross), no de por dónde entró el usuario a esta pantalla.
 *
 *  Se deriva de la URL en un solo lugar y no página por página: así ninguna
 *  pantalla puede quedarse sin migas por olvido, y las que cortan temprano
 *  («Cargando…», error) las siguen mostrando igual, porque la barra vive en
 *  el layout y no adentro del `return` de la página.
 *
 *  Dos excepciones, acotadas a propósito. `back` es la URL de la Matriz (con
 *  vista y filtros) en el momento en que el usuario entró a un resultado o
 *  un comparador — la manda quien linkea para acá (`MatrixPage`) como
 *  `location.state.back`. Si está, el tramo "Matriz" apunta ahí en vez de a
 *  `/matriz` a secas, así que "volver" (por acá o por la flecha del
 *  navegador) deja la Matriz como estaba, no reseteada. Sin ese estado
 *  (entrada directa, refresh) cae al link estructural de siempre.
 *
 *  `from` es un tramo entero antepuesto a la jerarquía estructural — para
 *  cuando se entra a una pantalla desde otra que no es su padre en esa
 *  jerarquía (el botón "Resultados" de una fila de `/runs` manda a
 *  `/matriz`, que estructuralmente no cuelga de Runs). Sin `from` la Matriz
 *  no tiene forma de decir "volviste de Runs" — con él, `location.state.from`
 *  se antepone tal cual. */
export function crumbsForPath(pathname: string, back?: string, from?: Crumb): Crumb[] {
  const [section, detail] = pathname.split('/').filter(Boolean)
  const withFrom = (crumbs: Crumb[]): Crumb[] => (from ? [from, ...crumbs] : crumbs)

  switch (section) {
    case undefined:
      return [{ label: 'Dashboard' }]
    case 'dashboard':
      return [{ label: 'Dashboard' }]
    case 'matriz':
      return withFrom([{ label: 'Matriz' }])
    // El resultado y el comparador se abren desde la matriz, así que cuelgan
    // de ella aunque su URL no lo diga.
    case 'resultados':
      return [{ label: 'Matriz', to: back ?? '/matriz' }, { label: 'Resultado' }]
    case 'comparador':
      return [{ label: 'Matriz', to: back ?? '/matriz' }, { label: 'Comparador' }]
    case 'runs':
      return detail ? [{ label: 'Runs', to: '/runs' }, { label: `#${detail}` }] : [{ label: 'Runs' }]
    case 'datos':
      return [{ label: 'Datos' }]
    case 'estrategias':
      return detail
        ? [{ label: 'Estrategias', to: '/estrategias' }, { label: detail }]
        : [{ label: 'Estrategias' }]
    case 'walk-forward':
      return [{ label: 'Walk-forward' }]
    case 'exchanges':
      return detail
        ? [{ label: 'Cuentas', to: '/exchanges' }, { label: `#${detail}` }]
        : [{ label: 'Cuentas' }]
    case 'alertas':
      return [{ label: 'Alertas' }]
    case 'bots':
      if (detail === 'historial') return [{ label: 'Bots', to: '/bots' }, { label: 'Historial' }]
      if (detail === 'transacciones') {
        return [{ label: 'Bots', to: '/bots' }, { label: 'Transacciones' }]
      }
      if (detail === 'calendario') return [{ label: 'Bots', to: '/bots' }, { label: 'Calendario' }]
      return detail
        ? [{ label: 'Bots', to: '/bots' }, { label: `#${detail}` }]
        : [{ label: 'Bots' }]
    case 'configuracion':
      return [{ label: 'Configuración' }]
    case 'papelera':
      return [{ label: 'Papelera' }]
    case 'sistema': {
      const system = { label: 'Sistema', to: '/sistema' }
      if (detail === 'usuarios') return [system, { label: 'Usuarios' }]
      if (detail === 'papelera') return [system, { label: 'Papelera' }]
      return [{ label: 'Salud y registros' }]
    }
    case 'ayuda':
      return [{ label: 'Ayuda' }]
    default:
      return [{ label: 'Dashboard' }]
  }
}

/** Nombre de `lib/icons.tsx#ICON_MAP` para el título del topbar — la misma
 *  sección de la URL que decide las migas, no una lista aparte que se
 *  pueda desincronizar de `crumbsForPath`. Resultado y comparador cuelgan
 *  del ícono de Matriz, igual que cuelgan de ella en las migas. */
export function sectionIconForPath(pathname: string): string {
  const [section] = pathname.split('/').filter(Boolean)
  switch (section) {
    case 'dashboard':
      return 'dashboard'
    case 'resultados':
    case 'comparador':
    case 'matriz':
      return 'matrix'
    case 'runs':
      return 'runs'
    case 'datos':
      return 'data'
    case 'estrategias':
      return 'strategies'
    case 'walk-forward':
      return 'walkforward'
    case 'exchanges':
      return 'exchanges'
    case 'alertas':
      return 'alerts'
    case 'bots':
      return 'bots'
    case 'configuracion':
      return 'settings'
    case 'sistema':
      return 'operations'
    case 'papelera':
      return 'trash'
    case 'ayuda':
      return 'help'
    default:
      return 'dashboard'
  }
}

/** Exportado porque `AppLayout` es quien ahora provee el valor — el título
 *  del topbar necesita el mismo nombre dinámico que las migas, y el topbar
 *  vive fuera de `BreadcrumbBar` (es un hermano, no un padre). Antes cada
 *  uno vivía en su propio lugar: `BreadcrumbBar` con el estado y el
 *  Provider, `AppLayout` calculando el título del topbar directo de
 *  `crumbsForPath`, sin enterarse nunca de este override — por eso el
 *  título tapaba "#2" en vez del nombre real ya resuelto. */
export const SetLabelContext = createContext<(label: string | undefined) => void>(() => {})

/** Reemplaza la etiqueta del último tramo con el nombre de verdad de lo que se
 *  está viendo: la URL sólo tiene el id o el slug (`#42`, `golden_cross`) y el
 *  nombre recién se conoce cuando llegó el fetch. Llamarlo arriba de todo en la
 *  página, antes de cualquier `return` temprano — mientras `label` sea
 *  undefined queda la etiqueta que salió de la URL. */
export function useBreadcrumbLabel(label: string | undefined) {
  const setLabel = useContext(SetLabelContext)
  useEffect(() => {
    setLabel(label)
    return () => setLabel(undefined)
  }, [label, setLabel])
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav className="breadcrumbs" aria-label="Ruta de navegación">
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        return (
          <span key={index} className="breadcrumbs__item">
            {item.to && !isLast ? (
              <Link className="breadcrumbs__link" to={item.to}>
                {item.label}
              </Link>
            ) : (
              <span className={isLast ? 'breadcrumbs__current' : undefined}>{item.label}</span>
            )}
            {!isLast && (
              <span className="breadcrumbs__separator" aria-hidden="true">
                ›
              </span>
            )}
          </span>
        )
      })}
    </nav>
  )
}

/** Barra de migas fija del layout. `label`, si está, reemplaza el último
 *  tramo — lo resuelve `AppLayout` (el mismo valor que ya usa para el
 *  título del topbar, ver el comentario en `SetLabelContext`), no un
 *  estado propio: las dos cosas tienen que decir siempre lo mismo. */
export function BreadcrumbBar({ label }: { label?: string }) {
  const location = useLocation()
  const state = location.state as { back?: string; from?: Crumb } | null
  const back = state?.back
  const from = state?.from

  const items = crumbsForPath(location.pathname, back, from)
  const resolved = label
    ? items.map((item, index) => (index === items.length - 1 ? { ...item, label } : item))
    : items

  return (
    <div className="breadcrumb-bar">
      <Breadcrumbs items={[{ label: 'Deltabot', to: '/dashboard' }, ...resolved]} />
    </div>
  )
}
